import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdminUnits, useOverrideUnit, useResetEquipment } from '@/api/hooks/useAdminUnits';
import { useDashboardSummary } from '@/api/hooks/useAdmin';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Activity, Wrench, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import PiHealthCard from './PiHealthCard';
import { formatDateTime } from '@/lib/format';
import type { UnitStatus, UnitOverrideAction, AffectedBookingDto } from '@/api/types';

const STATUS_BADGE: Record<UnitStatus, { color: string; dot: string }> = {
  ONLINE: { color: 'bg-green-100 text-green-800', dot: 'bg-green-500' },
  OFFLINE: { color: 'bg-red-100 text-red-800', dot: 'bg-red-500' },
  DEGRADED: { color: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500' },
  MAINTENANCE: { color: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' },
};

type ConfirmAction = {
  unitId: string;
  unitName: string;
  type: 'override' | 'reset';
  action?: UnitOverrideAction;
  label: string;
};

export default function UnitsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [locationFilter, setLocationFilter] = useState('all');
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [reason, setReason] = useState('');
  const [affectedBookings, setAffectedBookings] = useState<AffectedBookingDto[]>([]);

  const { data: dashboardSummary } = useDashboardSummary();
  const locations = dashboardSummary?.locationBreakdown ?? [];

  const { data, isLoading } = useAdminUnits(
    locationFilter !== 'all' ? locationFilter : undefined,
  );
  const units = data?.units ?? [];

  const overrideMutation = useOverrideUnit();
  const resetMutation = useResetEquipment();

  const locationLabel =
    locationFilter === 'all'
      ? t('admin.all_locations')
      : locations.find((l) => l.locationId === locationFilter)?.locationName ??
        t('admin.all_locations');

  const handleConfirm = async () => {
    if (!confirmAction) return;
    try {
      if (confirmAction.type === 'override' && confirmAction.action) {
        const result = await overrideMutation.mutateAsync({
          unitId: confirmAction.unitId,
          action: confirmAction.action,
          reason: reason || confirmAction.label,
        });
        if (result.affectedBookings && result.affectedBookings.length > 0) {
          setAffectedBookings(result.affectedBookings);
        } else {
          toast.success(t('admin.action_success'));
        }
      } else if (confirmAction.type === 'reset') {
        await resetMutation.mutateAsync({
          unitId: confirmAction.unitId,
          reason: reason || 'Equipment reset',
        });
        toast.success(t('admin.reset_success'));
      }
    } catch (err: unknown) {
      const appErr = err as { status?: number; code?: string; message?: string };
      if (appErr.status === 409) {
        toast.error(t('admin.reset_blocked_active'));
      } else {
        toast.error(appErr.message || t('common.error'));
      }
    }
    setConfirmAction(null);
    setReason('');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold">{t('admin.units')}</h1>
        {locations.length > 1 && (
          <Select value={locationFilter} onValueChange={(v) => setLocationFilter(v ?? 'all')}>
            <SelectTrigger>
              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-sm">{locationLabel}</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('admin.all_locations')}</SelectItem>
              {locations.map((loc) => (
                <SelectItem key={loc.locationId} value={loc.locationId}>
                  {loc.locationName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Location Pi transport health (Sprint 10a Evening 1 UI) — only when a specific location is selected.
          One Pi serves a whole facility, so its state belongs to the location, not a unit. Its own query +
          loading state (see useLocationPiHealth): a slow/failed pi-connection read never slows this list. */}
      {locationFilter !== 'all' && <PiHealthCard locationId={locationFilter} />}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      ) : units.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          {t('admin.no_units')}
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {units.map((unit) => {
            const badge = STATUS_BADGE[unit.status] ?? STATUS_BADGE.ONLINE;
            return (
              <Card
                key={unit.id}
                className={`cursor-pointer transition-all hover:bg-accent/50 ${unit.currentSession ? 'ring-2 ring-[#C2713A] shadow-md' : ''}`}
                onClick={() => navigate(`/admin/units/${unit.id}`)}
              >
                <CardContent className="p-5">
                  {/* Header: name + status */}
                  <div className="mb-3 flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold">{unit.name}</h3>
                      <p className="text-xs text-muted-foreground">
                        {t(`sport_${unit.sportCode}`, { defaultValue: unit.sportName })}
                      </p>
                    </div>
                    {unit.currentSession ? (
                      <Badge className="text-xs bg-[#C2713A] text-white animate-pulse">
                        IN USE
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className={`text-xs ${badge.color}`}>
                        <span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${badge.dot}`} />
                        {t(`admin.${unit.status.toLowerCase()}`)}
                      </Badge>
                    )}
                  </div>

                  {/* Location */}
                  <p className="mb-3 text-xs text-muted-foreground truncate">
                    {unit.locationName}
                  </p>

                  {/* Session info */}
                  <div className="mb-4 min-h-[40px]">
                    {unit.currentSession ? (
                      <div className="rounded-md bg-orange-50 p-2 text-xs border border-orange-200">
                        <div className="flex items-center gap-1.5 font-medium text-orange-800">
                          <Activity className="h-3 w-3" />
                          {unit.currentSession.customerName}
                        </div>
                        <p className="mt-0.5 text-orange-600">
                          {t('admin.ends_at')} {new Date(unit.currentSession.endTime).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit', hour12: true })}
                        </p>
                        <div className="mt-1.5 h-1 bg-orange-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#C2713A] transition-all"
                            style={{ width: `${Math.min(100, Math.max(0, ((Date.now() - new Date(unit.currentSession.startTime).getTime()) / (new Date(unit.currentSession.endTime).getTime() - new Date(unit.currentSession.startTime).getTime())) * 100))}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {t('admin.available')}
                      </p>
                    )}
                  </div>

                  {/* Quick actions */}
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    {unit.status === 'MAINTENANCE' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() =>
                          setConfirmAction({
                            unitId: unit.id,
                            unitName: unit.name,
                            type: 'override',
                            action: 'CLEAR_MAINTENANCE',
                            label: t('admin.clear_maintenance'),
                          })
                        }
                      >
                        <Wrench className="mr-1 h-3 w-3" />
                        {t('admin.clear_maintenance')}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() =>
                          setConfirmAction({
                            unitId: unit.id,
                            unitName: unit.name,
                            type: 'override',
                            action: 'SET_MAINTENANCE',
                            label: t('admin.set_maintenance'),
                          })
                        }
                      >
                        <Wrench className="mr-1 h-3 w-3" />
                        {t('admin.set_maintenance')}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setConfirmAction({
                          unitId: unit.id,
                          unitName: unit.name,
                          type: 'reset',
                          label: t('admin.reset'),
                        })
                      }
                    >
                      <RotateCcw className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Confirmation dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) { setConfirmAction(null); setReason(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.label} — {confirmAction?.unitName}
            </DialogTitle>
          </DialogHeader>
          <div>
            <Label>{t('admin.reason')}</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('admin.reason_placeholder')}
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmAction(null); setReason(''); }}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleConfirm} disabled={overrideMutation.isPending || resetMutation.isPending}>
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance warning — affected bookings */}
      <Dialog open={affectedBookings.length > 0} onOpenChange={(open) => { if (!open) setAffectedBookings([]); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin.maintenance_warning')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{t('admin.affected_bookings')}</p>
            {affectedBookings.map((b) => (
              <div key={b.bookingId} className="rounded-md border p-2 text-sm">
                <span className="font-medium">{b.customerEmail}</span>
                <span className="ml-2 text-muted-foreground">
                  {formatDateTime(b.startTime, i18n.language)} — {b.status}
                </span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAffectedBookings([])}>
              {t('admin.maintenance_keep_as_is')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
